import { useEffect, useState } from "react";

import { useMe, useProfiles } from "./api/hooks";
import { useViewportHeight } from "./lib/useViewportHeight";
import { useMediaQuery } from "./lib/useMediaQuery";
import { Sidebar } from "./components/Sidebar";
import { Topbar } from "./components/Topbar";
import { VerifyEmailPage } from "./views/AuthPages";
import { ChatView } from "./views/ChatView";
import { DbView, Skeleton } from "./views/DbView";
import { Login } from "./views/Login";
import { Schaerfen } from "./views/Schaerfen";
import { Historie } from "./views/Historie";

/** Views that must fit the window instead of growing past it. */
const FILLING_VIEWS = new Set(["foerderungen", "technologie", "chat-f", "chat-t"]);

function useHashView(): string {
  // Anything after the first segment belongs to the view itself — the
  // database page keeps its tab there.
  const [view, setView] = useState(() => hashView());
  useEffect(() => {
    const onChange = () => setView(hashView());
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return view;
}

function hashView(): string {
  const raw = location.hash.replace("#/", "");
  return raw.split("?")[0] || "foerderungen";
}

export function App() {
  useViewportHeight();
  // Email links land on real paths; everything else is hash-routed like the
  // blueprint.
  if (location.pathname.startsWith("/verify-email")) return <VerifyEmailPage />;
  return <AuthGate />;
}

function AuthGate() {
  const me = useMe();

  if (me.isPending) {
    return (
      <div className="auth-wrap">
        <img src="/logo.png" alt="Autoflug" className="auth-logo" />
      </div>
    );
  }
  if (me.isError) return <div className="auth-wrap"><div className="auth-card" role="alert">
    <h1>Verbindung fehlgeschlagen</h1><p>Die Anmeldung konnte nicht geprüft werden.</p>
    <button className="btn btn-block" onClick={() => void me.refetch()}>Erneut versuchen</button>
  </div></div>;
  if (!me.data) return <Login />;
  return <Shell />;
}

function Shell() {
  const me = useMe();
  const view = useHashView();
  const profiles = useProfiles(true);
  // Die Schublade auf Mobilgeraeten.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const compact = useMediaQuery("(max-width: 1100px)");
  // Am Schreibtisch klappt derselbe Knopf die Leiste ein statt sie
  // hereinzuschieben — und die Wahl bleibt, denn wer sie einmal weghaben
  // wollte, will das beim naechsten Mal meistens wieder.
  const [railCollapsed, setRailCollapsed] = useState(
    () => localStorage.getItem("rp_rail") === "zu",
  );

  useEffect(() => {
    localStorage.setItem("rp_rail", railCollapsed ? "zu" : "auf");
  }, [railCollapsed]);
  const [navOpen, setNavOpen] = useState<Record<"f" | "t", boolean>>({ f: true, t: false });

  // Opening a group's view expands its subtree, exactly like the blueprint.
  useEffect(() => {
    if (["foerderungen", "chat-f", "schaerfen-f", "zustand-f"].includes(view)) {
      setNavOpen((state) => ({ ...state, f: true }));
    }
    if (["technologie", "chat-t", "schaerfen-t", "zustand-t"].includes(view)) {
      setNavOpen((state) => ({ ...state, t: true }));
    }
    setSidebarOpen(false);
  }, [view]);

  const funding = profiles.data?.find((p) => p.mode === "funding");
  const technology = profiles.data?.find((p) => p.mode === "technology");

  const body = () => {
    if (profiles.isPending) return <Skeleton />;
    switch (view) {
      case "technologie":
        return <DbView type="technology" profile={technology} />;
      case "chat-f":
        return funding ? <ChatView profile={funding} key={funding.id} /> : <MissingProfile />;
      case "chat-t":
        return technology ? (
          <ChatView profile={technology} key={technology.id} />
        ) : (
          <MissingProfile />
        );
      case "schaerfen-f":
        return funding ? <Schaerfen profile={funding} key={funding.id} /> : <MissingProfile />;
      case "schaerfen-t":
        return technology ? (
          <Schaerfen profile={technology} key={technology.id} />
        ) : (
          <MissingProfile />
        );
      case "zustand-f":
        return funding ? <Historie profile={funding} /> : <MissingProfile />;
      case "zustand-t":
        return technology ? <Historie profile={technology} /> : <MissingProfile />;
      case "foerderungen":
      default:
        return <DbView type="funding" profile={funding} />;
    }
  };

  return (
    <div id="shell" className={railCollapsed ? "rail-zu" : undefined}>
      {me.data && (
        <Topbar
          user={me.data}
          railCollapsed={compact ? !sidebarOpen : railCollapsed}
          onToggleSidebar={() => {
            // Ein Knopf, zwei Bedeutungen — je nachdem, ob die Leiste im
            // Raster steht oder als Schublade darueberliegt.
            if (compact) {
              setSidebarOpen((open) => !open);
            } else {
              setRailCollapsed((zu) => !zu);
            }
          }}
        />
      )}
      {compact && sidebarOpen && <button className="sidebar-backdrop" aria-label="Navigation schließen" onClick={() => setSidebarOpen(false)} />}
      <Sidebar
        compact={compact}
        onClose={() => setSidebarOpen(false)}
        view={view}
        open={sidebarOpen}
        navOpen={navOpen}
        onToggleGroup={(key) => setNavOpen((state) => ({ ...state, [key]: !state[key] }))}
      />
      <main id="main" inert={compact && sidebarOpen}>
        {/* Database and chat views fill the frame exactly and scroll inside
            their own columns; the remaining views scroll as a page. */}
        <section className={"view active" + (FILLING_VIEWS.has(view) ? " view-fills" : "")}>
          {body()}
        </section>
      </main>
    </div>
  );
}

function MissingProfile() {
  return (
    <div className="state-block">
      <div className="glyph">∅</div>
      <h3>Profil fehlt</h3>
      <p>
        Das Basisprofil wurde nicht gefunden. Anlegen mit:
        <span className="mono"> manage.py seed_baseline</span>
      </p>
    </div>
  );
}
